/**
 * v3.18-1264 PAYMENT-007C — Enter New Credit Card dialog.
 *
 * Admin/Director-only manual entry path. Posts to /api/card-submission
 * with a `source="hub-manual"` marker so audit + retry queues distinguish
 * it from EQF submissions. The client match/link flow works identically:
 * the backend uses the passed email/phone to auto-link. If we already
 * know the client id, we tag it via a follow-up link call so the row
 * is guaranteed to be attached even when the backend match rules miss.
 */
import { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { CreditCard, Loader2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export const EnterNewCreditCardDialog = ({ open, onOpenChange, clientId, clientEmail, clientPhone, clientName, onSaved }) => {
  const [cardNumber, setCardNumber] = useState('');
  const [exp, setExp] = useState('');
  const [cvv, setCvv] = useState('');
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [zip, setZip] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setCardNumber(''); setExp(''); setCvv(''); setName('');
    setAmount(''); setZip(''); setSubmitting(false);
  };

  const cleanNumber = cardNumber.replace(/\s+/g, '');
  const canSubmit = (
    cleanNumber.length >= 13 &&
    /^\d{4}$/.test(exp) &&
    cvv.length >= 3 &&
    !!name.trim() &&
    parseFloat(amount || '0') > 0 &&
    !submitting
  );

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await api.post('/card-submission', {
        card_number: cleanNumber,
        exp,
        cvv,
        name: name.trim(),
        amount: parseFloat(amount),
        zip: zip || null,
        email: clientEmail || null,
        phone: clientPhone || null,
        source: 'hub-manual',
      });
      const recordId = res?.data?.id;
      const linkedClientId = res?.data?.client_id;
      // If the backend couldn't match by email/phone but we know the client,
      // link the fresh record explicitly so the new card lands on this page.
      if (recordId && (!linkedClientId || linkedClientId !== clientId) && clientId) {
        try {
          await api.post(`/credit-card-auth/${recordId}/link`, { client_id: clientId });
        } catch { /* ignore — record is still recoverable via unlinked queue */ }
      }
      toast.success('New credit card saved.');
      onSaved?.();
      onOpenChange(false);
      reset();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to save card';
      toast.error(typeof msg === 'string' ? msg : 'Failed to save card');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!submitting) { if (!v) reset(); onOpenChange(v); } }}>
      <DialogContent className="max-w-md" data-testid="enter-new-cc-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <CreditCard className="h-4 w-4" />
            Enter New Credit Card
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500 flex items-start gap-1.5">
            <ShieldAlert className="h-3 w-3 mt-0.5 text-rose-600" />
            Sensitive card data is transmitted over TLS and auto-purges after 12 hours. All access is audited.
          </p>
          {clientName ? (
            <p className="text-xs text-slate-600">Attach to <span className="font-medium">{clientName}</span></p>
          ) : null}
          <div>
            <Label htmlFor="new-cc-name">Name on card</Label>
            <Input
              id="new-cc-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Cardholder name"
              data-testid="new-cc-name"
            />
          </div>
          <div>
            <Label htmlFor="new-cc-number">Card number</Label>
            <Input
              id="new-cc-number"
              inputMode="numeric"
              autoComplete="cc-number"
              value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value.replace(/[^\d ]/g, ''))}
              placeholder="•••• •••• •••• ••••"
              data-testid="new-cc-number"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="new-cc-exp">Exp MMYY</Label>
              <Input
                id="new-cc-exp"
                inputMode="numeric"
                autoComplete="cc-exp"
                maxLength={4}
                value={exp}
                onChange={(e) => setExp(e.target.value.replace(/[^\d]/g, ''))}
                placeholder="MMYY"
                data-testid="new-cc-exp"
              />
            </div>
            <div>
              <Label htmlFor="new-cc-cvv">CVV</Label>
              <Input
                id="new-cc-cvv"
                inputMode="numeric"
                autoComplete="cc-csc"
                maxLength={4}
                value={cvv}
                onChange={(e) => setCvv(e.target.value.replace(/[^\d]/g, ''))}
                placeholder="•••"
                data-testid="new-cc-cvv"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="new-cc-amount">Amount</Label>
              <Input
                id="new-cc-amount"
                type="number"
                step="0.01"
                min="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                data-testid="new-cc-amount"
              />
            </div>
            <div>
              <Label htmlFor="new-cc-zip">Billing ZIP</Label>
              <Input
                id="new-cc-zip"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                placeholder="Optional"
                data-testid="new-cc-zip"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="new-cc-submit-btn"
          >
            {submitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
            Save Card
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EnterNewCreditCardDialog;
